import { IChildPageProps } from "./ChildPage.types";
import { ModelCompareV4 } from "@ubnt/store.core/lib/components/ModelCompareV4/ModelCompareV4";
import { useMicrositeProductPage } from "@ubnt/store.core/lib/components/Microsites/context/MicrositeProductPageContext";
import * as S from "./ChildPage.styles";
import React, { useMemo, useState } from "react";
import type { SelectedProduct } from "@ubnt/store.core/lib/constants/modelCompareData/types";
import { createBaseCompareState } from "./utils";
import { StyledContainer } from "components/StyledContainer";
import { MODEL_COMPARE_STICK_OFFSET_CORRECTION } from "util/constants";
import { MicrositeHeading } from "pages/LayoutComponents/MicrositeHeading";
import { useTranslation } from "hooks/useTranslation";
import { InstallationAddonCarouselInView } from "components/InstallationAddonsCarousel/InstallationAddonCarouselInView";
import { CondensedFeatureGridInView } from "pages/LayoutComponents/CondensedFeatureGrid/CondensedFeatureGridInView";

export const ChildPage = ({
  defaultCompareSection,
  featureCards,
  installationAddonsConfig,
  groupId,
  defaultSelectedProductCount,
  shouldForceHideCompareSection,
  customPageBottomPadding = 96,
}: Readonly<IChildPageProps>) => {
  const { t } = useTranslation();
  const { showBuyButton } = useMicrositeProductPage();

  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    createBaseCompareState(
      defaultCompareSection,
      defaultSelectedProductCount,
      groupId,
    ) as SelectedProduct[],
  );

  const productIdMap = useMemo(() => {
    return selectedProducts.map(({ productId }) => String(productId));
  }, [selectedProducts]);

  const selectedInstallationAddons = useMemo(() => {
    return installationAddonsConfig.filter(({ tags }) =>
      tags.some(({ id }) => productIdMap.includes(id)),
    );
  }, [installationAddonsConfig, productIdMap]);

  return (
    <S.ContentWrapper>
      {!shouldForceHideCompareSection && (
        <StyledContainer
          size="desktop"
          noVerticalMargin
          $spacing={{ top: 36, bottom: 0 }}
        >
          <ModelCompareV4
            showBuyButton={showBuyButton}
            section={{ ...defaultCompareSection, showProductGroups: true }}
            selectedProducts={selectedProducts}
            headerStickOffset={({ maxImageHeight }) =>
              maxImageHeight + MODEL_COMPARE_STICK_OFFSET_CORRECTION
            }
            setSelectedProducts={setSelectedProducts}
          />
        </StyledContainer>
      )}

      {!!featureCards?.length && (
        <StyledContainer
          size="desktop"
          noVerticalMargin
          $spacing={{
            top: 64,
            bottom: !!selectedInstallationAddons?.length ? 32 : 96,
          }}
        >
          <CondensedFeatureGridInView cards={featureCards} />
        </StyledContainer>
      )}

      {!!selectedInstallationAddons.length && (
        <StyledContainer
          size="desktop"
          noVerticalMargin
          $spacing={{
            top: !!featureCards?.length ? 40 : 72,
            bottom: customPageBottomPadding ?? 96,
          }}
        >
          <MicrositeHeading
            title={t("commons.installationEssentials")}
            spacing={{ top: 0, bottom: 24 }}
            isSmallDescription
          />
          <InstallationAddonCarouselInView items={selectedInstallationAddons} />
        </StyledContainer>
      )}
    </S.ContentWrapper>
  );
};
